// components/ButtonGuide.tsx
import { ContentCopy } from "@mui/icons-material";
import { Box, Typography, Tooltip, Paper, IconButton, Divider, CircularProgress } from "@mui/material";
import { Button, useNotify } from "react-admin";
import { BreadcrumbNav } from "../BreadCrumbNav";
import { GhostButton } from "@/components/common/GhostButton";
import { AlertType } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";

export const ButtonGuide = () => {
  const { theme, isDarkMode } = useAppTheme();
  const notify = useNotify();

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    notify("Copied to clipboard!", { type: AlertType.SUCCESS });
  };

  const buttons = [
    {
      label: "Primary (CTA)",
      description: "Use for main actions like Submit, Save, or Confirm.",
      code: '<Button label="Submit" variant="contained" />',
      states: [
        {
          label: "Default",
          element: <Button label="Submit" variant="contained" />,
        },
        {
          label: "Hover",
          element: (
            <Button
              label="Submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              }}
            />
          ),
        },
        {
          label: "Disabled",
          element: <Button label="Submit" variant="contained" disabled />,
        },
        {
          label: "Loading",
          element: (
            <Button
              label="Submit"
              variant="contained"
              disabled
              startIcon={<CircularProgress size={16} color="inherit" />}
            />
          ),
        },
      ],
    },
    {
      label: "Secondary",
      description: "Use for secondary actions like Cancel or Back.",
      code: '<Button label="Cancel" variant="outlined" />',
      states: [
        {
          label: "Default",
          element: <Button label="Cancel" variant="outlined" />,
        },
        {
          label: "Hover",
          element: (
            <Button
              label="Cancel"
              variant="outlined"
              sx={{
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              }}
            />
          ),
        },
        {
          label: "Disabled",
          element: <Button label="Cancel" variant="outlined" disabled />,
        },
        {
          label: "Loading",
          element: (
            <Button
              label="Cancel"
              variant="outlined"
              disabled
              startIcon={<CircularProgress size={16} color="inherit" />}
            />
          ),
        },
      ],
    },
    {
      label: "Tertiary",
      description: "Use for low-priority actions like Learn More.",
      code: '<Button label="Learn More" variant="text" />',
      states: [
        {
          label: "Default",
          element: <Button label="Learn More" variant="text" />,
        },
        {
          label: "Hover",
          element: <Button label="Learn More" variant="text" sx={{ backgroundColor: theme.palette.action.hover }} />,
        },
        {
          label: "Disabled",
          element: <Button label="Learn More" variant="text" disabled />,
        },
        {
          label: "Loading",
          element: (
            <Button
              label="Learn More"
              variant="text"
              disabled
              startIcon={<CircularProgress size={16} color="inherit" />}
            />
          ),
        },
      ],
    },
    {
      label: "Ghost",
      description: "Use for non-primary, non-destructive actions like 'Create Manually' or alternatives in modals.",
      code: '<GhostButton label="Create Manually" />',
      states: [
        {
          label: "Default",
          element: <GhostButton label="Create Manually" />,
        },
        {
          label: "Hover",
          element: (
            <GhostButton
              label="Create Manually"
              sx={{
                borderColor: "rgba(0, 0, 0, 0.12)",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }}
            />
          ),
        },
        {
          label: "Disabled",
          element: <GhostButton label="Create Manually" disabled />,
        },
        {
          label: "Loading",
          element: (
            <GhostButton label="Create Manually" disabled startIcon={<CircularProgress size={16} color="inherit" />} />
          ),
        },
      ],
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <BreadcrumbNav current="Buttons" />
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Buttons
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Below are styled button variants available in the design system, along with recommended usage and copyable
          code snippets.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={4}>
        {buttons.map(({ label, description, code, states }) => (
          <Paper
            key={label}
            elevation={1}
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4">{label}</Typography>
              <Tooltip title="Copy JSX snippet">
                <IconButton onClick={() => handleCopy(code)} size="small">
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {states.map(({ label, element }) => (
                <Box key={label} display="flex" alignItems="center" gap={2} sx={{ pl: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                    {label}
                  </Typography>
                  {element}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                p: 2,
                mt: 2,
                fontFamily: "monospace",
                fontSize: 14,
                borderRadius: 1,
              }}
            >
              {code}
            </Box>
          </Paper>
        ))}
        <Divider sx={{ mt: 4 }} />
      </Box>
    </Box>
  );
};
